/**
 * ContentCommentsContent
 *
 * Main UI for the comments sheet: header, scrollable list, and input bar.
 * Mirrors Flutter's comments_bottom_sheet.dart body.
 */

import React, { useCallback, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ExpauseLoadingIndicator } from '@/presentation/components/common/loadingIndicators/ExpauseLoadingIndicator';
import {
  VirtualList,
  type VirtualListHandle,
} from '@/presentation/components/common/virtualList/VirtualList';
import { qaAttr } from '@/presentation/utils/qaAttr';
import { useContentComments } from './hooks/useContentComments';
import { useCommentVideoReply } from './hooks/useCommentVideoReply';
import { CommentRow } from './components/CommentRow';
import { CommentInput, type CommentInputHandle } from './components/CommentInput';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import type { CommentInfoEntity } from '@/domain/entities/comment/commentInfoEntity';
import type { CommentEntity } from '@/domain/entities/comment/commentEntity';

export interface ContentCommentsContentProps {
  media: UserMediaEntity;
  commentInfo?: CommentInfoEntity;
  screenKey?: string;
}

export const ContentCommentsContent: React.FC<ContentCommentsContentProps> = ({
  media,
  commentInfo,
  screenKey,
}) => {
  const { theme } = useTheme();
  const { l10n } = useLocale();
  const responsive = useResponsiveDesign();

  const {
    comments,
    isLoading,
    commentCount,
    commentToReply,
    highlightedCommentId,
    loadMore,
    expandReplies,
    collapseReplies,
    addComment,
    likeComment,
    setCommentToReply,
    cancelReply,
    videoReplyIsAvailable,
    watchCommentMedia,
  } = useContentComments({ media, commentInfo });

  const listRef = useRef<VirtualListHandle>(null);
  const scrolledToHighlightRef = useRef<string | null>(null);
  const commentInputRef = useRef<CommentInputHandle>(null);

  // Composer-draft seam for the video reply: Flutter reads the controller text
  // at comment-build time (comments_bottom_sheet.dart:468) and clears it in the
  // same block (:516). The ref identity is stable, so empty deps are correct.
  const getDraftText = useCallback(() => commentInputRef.current?.getValue() ?? '', []);
  const clearDraft = useCallback(() => commentInputRef.current?.clear(), []);

  // Surface half of the video-reply flow (Flutter `_sendMediaMessage`, :891-930).
  // It opens the add-content sheet, so it lives outside `useContentComments`
  // (which stays surface-agnostic) and gets the business half injected here.
  const { startVideoReply } = useCommentVideoReply({
    media,
    commentToReply,
    addComment,
    watchCommentMedia,
    getDraftText,
    clearDraft,
    screenKey,
  });

  // Scroll the highlighted (deep-linked) comment into view. With VirtualList
  // the target row may be off-screen / unmounted, so we drive the scroll
  // through the imperative scrollToIndex API rather than a DOM ref.
  //
  // The effect still re-evaluates on `comments` changes: on the very first
  // non-empty commit the target row may not yet exist in the VirtualList DOM,
  // so the first scrollToIndex can no-op and must be retried on the next
  // commit. The scrolledToHighlightRef guard commits the scroll exactly ONCE
  // per highlighted id, so later `comments` mutations (like toggle, expand /
  // collapse replies, optimistic add, pagination) no longer re-yank the list
  // back to the deep-linked comment. (NOTE: this intentionally diverges from
  // the sibling RoadmapCommentsContent, which still re-fires on every change.)
  useEffect(() => {
    if (!highlightedCommentId) return;
    if (scrolledToHighlightRef.current === highlightedCommentId) return;
    const index = comments.findIndex(c => c.id === highlightedCommentId);
    if (index >= 0) {
      scrolledToHighlightRef.current = highlightedCommentId;
      listRef.current?.scrollToIndex(index, 'smooth');
    }
  }, [highlightedCommentId, comments]);

  const handleSend = async (text: string): Promise<boolean> => {
    const ok = await addComment(text);
    if (!ok) toast.error(l10n.comments.errorSending);
    return ok;
  };

  const handleLike = async (comment: CommentEntity, liked: boolean) => {
    try {
      await likeComment(comment, liked);
    } catch {
      toast.error(l10n.comments.errorLiking);
    }
  };

  // Coarse lifecycle token reusing existing state: spinner shown with no rows
  // yet is `loading`; settled with no rows is `empty`; otherwise `ready`.
  const sheetStatus =
    isLoading && comments.length === 0
      ? 'loading'
      : comments.length === 0
        ? 'empty'
        : 'ready';

  return (
    <div
      {...qaAttr({ id: 'comments-sheet', status: sheetStatus })}
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
      }}
    >
      <div
        {...qaAttr({ id: 'comments-sheet-title', value: commentCount })}
        style={{
          padding: responsive.spacing.lg,
          borderBottom: `${responsive.border.thin}px solid ${theme.border}`,
          textAlign: 'center',
          fontWeight: responsive.fontWeight.semibold,
          color: theme.text.primary,
          fontSize: responsive.font.base,
        }}
      >
        {l10n.comments.title} ({commentCount})
      </div>

      <div
        className="scrollbar-hide"
        style={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {isLoading && comments.length === 0 ? (
          <div style={{ padding: responsive.spacing.xl, display: 'flex', justifyContent: 'center' }}>
            <ExpauseLoadingIndicator />
          </div>
        ) : comments.length === 0 ? (
          <div
            style={{
              padding: responsive.spacing['3xl'],
              textAlign: 'center',
              color: theme.text.secondary,
              fontSize: responsive.font.sm,
            }}
          >
            {l10n.comments.noComments}
          </div>
        ) : (
          <>
            <VirtualList<CommentEntity>
              ref={listRef}
              qa={{ id: 'comments-list' }}
              items={comments}
              getItemKey={c => c.id}
              renderItem={c => (
                <CommentRow
                  comment={c}
                  highlightedCommentId={highlightedCommentId}
                  onReply={setCommentToReply}
                  onExpandReplies={expandReplies}
                  onCollapseReplies={collapseReplies}
                  onLike={handleLike}
                />
              )}
              gap={0}
              onReachedEnd={loadMore}
              reachedEndThreshold={200}
            />

            {isLoading && comments.length > 0 && (
              <div style={{ padding: responsive.spacing.base, display: 'flex', justifyContent: 'center' }}>
                <ExpauseLoadingIndicator />
              </div>
            )}
          </>
        )}
      </div>

      <CommentInput
        ref={commentInputRef}
        replyTo={commentToReply}
        onCancelReply={cancelReply}
        onSend={handleSend}
        showAddMedia={videoReplyIsAvailable}
        onMediaTap={startVideoReply}
        screenKey={screenKey}
      />
    </div>
  );
};
